import React from 'react';
import EarlyAccessModal from '../common/EarlyAccessModal';
import SEOHead from '../common/SEOHead';
import StructuredData from '../common/StructuredData';
import { SceneProvider } from '../lab/scene/SceneContext';
import SceneCanvas from '../lab/scene/SceneCanvas';
import LabNav from '../lab/LabNav';
import LabHero from '../lab/LabHero';
import ModelSection from '../lab/ModelSection';
import Convergence from '../lab/Convergence';
import Partnership from '../lab/Partnership';
import LanguagesBand from '../lab/LanguagesBand';
import Governance from '../lab/Governance';
import LabFooter from '../lab/LabFooter';
import { MODEL_SECTIONS } from '../lab/constants';
import { useLanguageDetection } from '../../hooks/useLanguageDetection';
import { useEarlyAccessModal } from '../../hooks/useEarlyAccessModal';

/**
 * "AI research lab" landing (design 5a): a fixed canvas scene renders the
 * day-cycle sky and the three model beings behind the scrolling content.
 * Sections register scene anchors via SceneProvider, so inserting new
 * sections between the existing ones keeps the choreography in sync.
 */
interface LandingProps {
  /** /early-access renders the same landing with the partnership form already open (ad links) */
  initialModalOpen?: boolean;
}

const Landing: React.FC<LandingProps> = ({ initialModalOpen = false }) => {
  useLanguageDetection();
  const { isOpen, openModal, closeModal } = useEarlyAccessModal(initialModalOpen);

  return (
    <SceneProvider>
      {/* overflow-x-clip (not hidden): hidden would make this the scroll container and break position:sticky */}
      <div className="min-h-screen overflow-x-clip bg-lab-night font-instrument text-lab-frost">
        <SEOHead />
        <StructuredData />
        <SceneCanvas />
        <LabNav onPartnerClick={openModal} />
        <main className="relative">
          <LabHero onPartnerClick={openModal} />
          {MODEL_SECTIONS.map((config) => (
            <ModelSection key={config.key} config={config} />
          ))}
          <Convergence onPartnerClick={openModal} />
          <Partnership onPartnerClick={openModal} />
          <LanguagesBand />
          <Governance onPartnerClick={openModal} />
        </main>
        <LabFooter />
        <EarlyAccessModal isOpen={isOpen} onClose={closeModal} />
      </div>
    </SceneProvider>
  );
};

export default Landing;
